import React, { useEffect } from 'react'
import { Field, WrappedFieldArrayProps, WrappedFieldProps } from 'redux-form'
import {
	Button, Checkbox, Form, Select
} from 'antd'
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { RootState } from '../../../reducers'
import { getUsers } from '../../../reducers/usersU/actions'
import { IPriceOfferReminder } from '../../../reducers/priceOffers/actions'

const { Option } = Select

// Prednastavene rychle volby (checkboxy) - pocet dni pred datumom "kedy sa ozvat"
const PRESET_DAYS_BEFORE = [0, 1, 2]

// Vsetky volby pre pocet dni v roletke (styl Google Calendar, iba dni)
const DAYS_BEFORE_OPTIONS = [0, 1, 2, 3, 7, 14]

const daysBeforeLabel = (daysBefore: number): string => {
	if (daysBefore === 0) return 'V deň'
	if (daysBefore === 1) return '1 deň predtým'
	if (daysBefore >= 2 && daysBefore <= 4) return `${daysBefore} dni predtým`
	if (daysBefore === 7) return 'Týždeň predtým'
	if (daysBefore === 14) return '2 týždne predtým'
	return `${daysBefore} dní predtým`
}

type UserOption = { value: number, label: string }

const useUserOptions = (): UserOption[] => {
	const dispatch = useDispatch()

	useEffect(() => {
		dispatch(getUsers(1, 10000, {}))
	}, [])

	return useSelector((state: RootState) => state.users.users.tableData)
		// iba pouzivatelia s e-mailom - pripomienka chodi na e-mail
		.filter((user: any) => Boolean(user?.email))
		.map((user: any) => ({
			value: user.id,
			label: user.lastName ? `${user.lastName} ${user.firstName}` : user.email
		}))
}

// redux-form Field pre vyber osoby (prijemcu pripomienky)
const RecipientSelectField = (props: WrappedFieldProps & { options: UserOption[] }) => {
	const { input, options } = props
	const { t } = useTranslation('components')

	return (
		<Form.Item className={'mb-0'} style={{ minWidth: 220, flex: 1 }}>
			<Select
				showSearch
				optionFilterProp={'children'}
				placeholder={t('components:Komu poslať pripomienku')}
				value={input.value || undefined}
				onChange={(value) => input.onChange(value)}
				onBlur={() => input.onBlur(input.value)}
				getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
			>
				{options.map((option) => (
					<Option key={option.value} value={option.value}>{option.label}</Option>
				))}
			</Select>
		</Form.Item>
	)
}

// redux-form Field pre vyber poctu dni pred datumom
const DaysBeforeSelectField = (props: WrappedFieldProps) => {
	const { input } = props
	return (
		<Form.Item className={'mb-0'} style={{ minWidth: 170 }}>
			<Select
				value={input.value === '' || input.value === null || input.value === undefined ? 0 : input.value}
				onChange={(value) => input.onChange(value)}
				onBlur={() => input.onBlur(input.value)}
				getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
			>
				{DAYS_BEFORE_OPTIONS.map((days) => (
					<Option key={days} value={days}>{daysBeforeLabel(days)}</Option>
				))}
			</Select>
		</Form.Item>
	)
}

type Props = WrappedFieldArrayProps<IPriceOfferReminder> & {
	defaultRecipientUserID: number | null,
}

const PriceOfferRemindersFields = (props: Props) => {
	const { fields, defaultRecipientUserID } = props
	const { t } = useTranslation('components')
	const userOptions = useUserOptions()

	const allReminders: IPriceOfferReminder[] = fields.getAll() || []

	const hasPreset = (daysBefore: number): boolean => allReminders.some((reminder) => Number(reminder?.daysBefore) === daysBefore)

	const togglePreset = (daysBefore: number, checked: boolean) => {
		if (checked) {
			fields.push({ recipientUserID: defaultRecipientUserID, daysBefore } as IPriceOfferReminder)
		} else {
			// zmaz vsetky pripomienky s tymto poctom dni (od konca, aby sa nepomiesali indexy)
			const all = fields.getAll() || []
			for (let index = all.length - 1; index >= 0; index -= 1) {
				if (Number(all[index]?.daysBefore) === daysBefore) {
					fields.remove(index)
				}
			}
		}
	}

	return (
		<div className={'mt-2'}>
			<div className={'flex direction-row'} style={{ gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
				{PRESET_DAYS_BEFORE.map((daysBefore) => (
					<Checkbox
						key={daysBefore}
						checked={hasPreset(daysBefore)}
						onChange={(e) => togglePreset(daysBefore, e.target.checked)}
					>
						{daysBeforeLabel(daysBefore)}
					</Checkbox>
				))}
			</div>

			{fields.map((field: string, index: number) => (
				<div
					key={field}
					className={'flex direction-row items-center'}
					style={{ gap: 8, marginBottom: 8 }}
				>
					<Field
						name={`${field}.daysBefore`}
						component={DaysBeforeSelectField}
					/>
					<Field
						name={`${field}.recipientUserID`}
						component={RecipientSelectField}
						options={userOptions}
					/>
					<Button
						type={'text'}
						danger
						icon={<DeleteOutlined />}
						onClick={() => fields.remove(index)}
					/>
				</div>
			))}

			<Button
				type={'link'}
				icon={<PlusOutlined />}
				style={{ paddingLeft: 0 }}
				onClick={() => fields.push({ recipientUserID: defaultRecipientUserID, daysBefore: 0 } as IPriceOfferReminder)}
			>
				{t('components:Pridať pripomienku')}
			</Button>
		</div>
	)
}

export default PriceOfferRemindersFields
